import React, { useEffect, useState } from 'react';
import { useCameraScanner } from '../hooks/useCameraScanner';
import { createPdfFromImages } from '../services/pdfGenerator';
import { Spinner } from './Spinner';
import { DeleteIcon } from './icons/DeleteIcon';
import { ScanEditorModal } from './ScanEditorModal';
import { EditIcon } from './icons/EditIcon';

interface ScannerModalProps {
    isOpen: boolean;
    onClose: () => void;
    onScanComplete: (pdfBlob: Blob) => void;
}

export const ScannerModal: React.FC<ScannerModalProps> = ({ isOpen, onClose, onScanComplete }) => {
    const { 
        videoRef, 
        canvasRef, 
        error, 
        capturedImages,
        setCapturedImages,
        startCamera, 
        stopCamera, 
        captureImage, 
        deleteImage,
        clearImages
    } = useCameraScanner();

    const [isProcessing, setIsProcessing] = useState(false);
    const [editingImageIndex, setEditingImageIndex] = useState<number | null>(null);

    useEffect(() => {
        if (isOpen) {
            startCamera();
        } else {
            stopCamera();
            clearImages(); // Clear images when modal is closed
        }
    }, [isOpen, startCamera, stopCamera, clearImages]);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && !isProcessing && editingImageIndex === null) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleKeyDown);
        }

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, onClose, isProcessing, editingImageIndex]);

    const handleFinish = async () => {
        if (capturedImages.length === 0) return;
        setIsProcessing(true);
        try {
            const pdfBlob = await createPdfFromImages(capturedImages);
            onScanComplete(pdfBlob);
        } catch (err) {
            console.error("Chyba při generování PDF:", err);
            // Optionally show an error message to the user
        } finally {
            setIsProcessing(false);
        }
    };

    const handleClose = () => {
        if (!isProcessing) {
            onClose();
        }
    };

    const handleUpdateImage = (index: number, newImageSrc: string) => {
        setCapturedImages(prev => {
            const newImages = [...prev];
            newImages[index] = newImageSrc;
            return newImages;
        });
        setEditingImageIndex(null);
    };

    if (!isOpen) return null;

    return (
        <>
            <div
                className="fixed inset-0 bg-black z-50 flex flex-col items-center justify-center text-white"
                role="dialog"
                aria-modal="true"
                aria-labelledby="scanner-title"
            >
                {isProcessing ? (
                    <Spinner text="Generuji PDF, prosím čekejte..." />
                ) : (
                    <>
                        <div className="absolute top-4 left-4 z-20">
                            <h2 id="scanner-title" className="text-xl font-bold">Skenovat účtenku</h2>
                        </div>
                        
                        <div className="relative w-full h-full flex items-center justify-center bg-black">
                            {error ? (
                                <div className="p-4 text-center">
                                    <p className="text-red-400">{error}</p>
                                    <button onClick={onClose} className="mt-4 px-4 py-2 bg-gray-700 rounded-md">Zavřít</button>
                                </div>
                            ) : (
                                <video ref={videoRef} playsInline autoPlay muted className="max-w-full max-h-full" />
                            )}
                            <canvas ref={canvasRef} className="hidden" />
                        </div>

                        {/* Bottom controls */}
                        <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 p-4 flex flex-col gap-4">
                            {/* Thumbnails */}
                            <div className="h-24 w-full overflow-x-auto whitespace-nowrap">
                                {capturedImages.map((imgSrc, index) => (
                                    <div key={index} className="group relative inline-block w-28 h-full mr-2 border-2 border-transparent hover:border-blue-500 rounded-md overflow-hidden">
                                        <img src={imgSrc} alt={`Snímek ${index + 1}`} className="w-full h-full object-cover" />
                                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all flex items-center justify-center">
                                             <button
                                                onClick={() => setEditingImageIndex(index)}
                                                className="p-1 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                                                aria-label={`Upravit snímek ${index + 1}`}
                                             >
                                                <EditIcon className="w-6 h-6" />
                                             </button>
                                        </div>
                                        <button
                                            onClick={() => deleteImage(index)}
                                            className="absolute top-0 right-0 bg-red-600 bg-opacity-80 rounded-bl-lg p-1 text-white hover:bg-opacity-100"
                                            aria-label={`Smazat snímek ${index + 1}`}
                                        >
                                            <DeleteIcon className="w-4 h-4" />
                                        </button>
                                    </div>
                                ))}
                            </div>

                            {/* Action buttons */}
                            <div className="flex items-center justify-around w-full">
                                <button onClick={handleClose} className="px-6 py-3 text-sm font-semibold rounded-md hover:bg-gray-700">
                                    Zrušit
                                </button>

                                <button
                                    onClick={captureImage}
                                    className="w-20 h-20 bg-white rounded-full border-4 border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-black focus:ring-white"
                                    aria-label="Pořídit snímek"
                                />

                                <button
                                    onClick={handleFinish}
                                    disabled={capturedImages.length === 0}
                                    className="px-6 py-3 bg-blue-600 text-sm font-semibold rounded-md hover:bg-blue-700 disabled:bg-gray-500 disabled:cursor-not-allowed"
                                >
                                    Dokončit ({capturedImages.length})
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </div>
            {editingImageIndex !== null && (
                <ScanEditorModal
                    isOpen={editingImageIndex !== null}
                    onClose={() => setEditingImageIndex(null)}
                    onConfirm={(newImageSrc) => handleUpdateImage(editingImageIndex, newImageSrc)}
                    imageSrc={capturedImages[editingImageIndex]}
                />
            )}
        </>
    );
};